import { ButtonInline } from '@remote-com/norma/core/button';
import { useRouter } from 'next/router';
import React from 'react';
import styled, { css } from 'styled-components';

import { colorStyle } from './config';

const Content = styled.span`
  white-space: pre-wrap;

  ${({ $underline }) =>
    $underline &&
    css`
      text-decoration: underline;
    `}

  ${({ $color }) =>
    $color &&
    css`
      ${colorStyle[$color]}
    `}
`;

export const CodeAnnotation = styled.code`
  color: var(--colors-irisBlue);
  background-color: var(--colors-selago);
  border-radius: 3px;
  font-size: 0.95em;
  padding: 0.2em 0.4em;
`;

function RenderAnnotations({ annotations, children }) {
  let wrappedChildren = <>{children}</>;

  if (annotations.code) {
    wrappedChildren = <CodeAnnotation>{wrappedChildren}</CodeAnnotation>;
  }

  if (annotations.strikethrough) {
    wrappedChildren = <del>{wrappedChildren}</del>;
  }

  if (annotations.italic) {
    wrappedChildren = <em>{wrappedChildren}</em>;
  }

  if (annotations.bold) {
    wrappedChildren = <strong>{wrappedChildren}</strong>;
  }

  wrappedChildren = (
    <Content $underline={annotations.underline} $color={annotations.color}>
      {wrappedChildren}
    </Content>
  );

  return wrappedChildren;
}

export function RenderHref({ href, children, internalHrefResolver }) {
  const router = useRouter();

  // Expects Notion link /45ee8d13687b47cea5ca6e2e45548c4b
  const notionInternalLinkRegExp = /^\/([A-z0-9-]*)/;
  const isInternalNotionLink = notionInternalLinkRegExp.test(href);

  if (isInternalNotionLink) {
    const notionInternalLinkExec = notionInternalLinkRegExp.exec(href);
    const internalRouteLink = internalHrefResolver({ href, id: notionInternalLinkExec[1], router });
    return <ButtonInline href={internalRouteLink}>{children}</ButtonInline>;
  }

  return <ButtonInline href={href}>{children}</ButtonInline>;
}

function defaultInternalHrefResolver({ href, router }) {
  return router.route.replace('/[id]', href);
}

export function RenderText({
  entries = [],
  replaceComponents,
  internalHrefResolver = defaultInternalHrefResolver,
}) {
  const { RenderHref: RenderHrefComponent = RenderHref } = replaceComponents || {};

  return entries.map((entry, index) => {
    const { type, href, annotations, plain_text: plainText } = entry;
    switch (type) {
      case 'text': {
        const { text } = entry;
        let wrappedContent = <>{text.content}</>;

        if (href) {
          wrappedContent = (
            <RenderHrefComponent href={href} internalHrefResolver={internalHrefResolver}>
              {wrappedContent}
            </RenderHrefComponent>
          );
        }

        return (
          <RenderAnnotations key={index} annotations={annotations}>
            {wrappedContent}
          </RenderAnnotations>
        );
      }

      case 'mention': {
        const { type: mentionType } = entry.mention;

        switch (mentionType) {
          case 'page': {
            let wrappedContent = <>{plainText === 'Untitled' ? '[Private page]' : plainText}</>;

            if (href) {
              wrappedContent = (
                <RenderHrefComponent href={href}>{wrappedContent}</RenderHrefComponent>
              );
            }

            return (
              <RenderAnnotations key={index} annotations={annotations}>
                {wrappedContent}
              </RenderAnnotations>
            );
          }

          case 'date': {
            const { end } = entry.mention.date;

            let wrappedContent = plainText;
            if (!end) {
              wrappedContent = wrappedContent.replace(' →', '');
            }

            if (href) {
              wrappedContent = (
                <RenderHrefComponent href={href}>{wrappedContent}</RenderHrefComponent>
              );
            }

            return (
              <RenderAnnotations key={index} annotations={annotations}>
                {wrappedContent}
              </RenderAnnotations>
            );
          }

          default:
            return <React.Fragment key={index}>{plainText}</React.Fragment>;
        }
      }

      default:
        return <React.Fragment key={index}>{plainText}</React.Fragment>;
    }
  });
}
